import type { AdminSavedChannels, AdminSavedChannelsRequest } from "@ternary/contracts"
import { Alert, Button, Card, Checkbox, Field, IconButton, Select } from "@ternary/ui"
import { type ReactElement, useState } from "react"
import { NavIcon } from "./nav-icons.js"

type Option = AdminSavedChannels["available"][number]

const KIND = { announcement: "Announcements", forum: "Forum", text: "Text" } as const
const BIT = { readMessageHistory: "Read Message History", viewChannel: "View Channel" } as const

// "A, B and C".
const listed = (names: readonly string[]) =>
  names.length < 2 ? (names[0] ?? "") : `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`

/** What the Ternary role needs before the bot can read a channel's posts. */
export function savedChannelHint(channel: Pick<Option, "missing" | "name">) {
  return `Allow ${listed(channel.missing.map((bit) => BIT[bit]))} for the Ternary role in #${channel.name}.`
}

/**
 * Saved posts' channels (Connections → Discord → Saved posts): the business picks the channels
 * whose posts Ternary saves, each with "Include thread replies" (on for a new one), sees whether
 * the bot can read each one and what to allow when it cannot, and whether Discord gives message
 * text yet. Save sends the whole list. It only shows: the panel saves through `onSave`.
 */
export function SavedChannelsCard({
  busy,
  onSave,
  view,
}: {
  busy: boolean
  onSave(channels: AdminSavedChannelsRequest["channels"]): void
  view: AdminSavedChannels
}): ReactElement {
  const [chosen, setChosen] = useState(() =>
    view.channels.map(({ channelId, guildId, includeReplies }) => ({
      channelId,
      guildId,
      includeReplies,
    })),
  )
  const [adding, setAdding] = useState("")
  const options = new Map(view.available.map((option) => [option.channelId, option]))
  const named = new Map(view.channels.map((channel) => [channel.channelId, channel]))
  const left = view.available.filter(
    ({ channelId }) => !chosen.some((channel) => channel.channelId === channelId),
  )
  const full = chosen.length >= 20
  const changed =
    JSON.stringify(chosen) !==
    JSON.stringify(
      view.channels.map(({ channelId, guildId, includeReplies }) => ({
        channelId,
        guildId,
        includeReplies,
      })),
    )

  return (
    <Card title="Saved channels">
      <div className="admin-stack admin-stack-tight">
        <p className="admin-note">
          Ternary saves every post in these channels: its text, files, channel, time and poster.
          Nothing outside them is stored.
        </p>
        {view.contentAvailable ? null : (
          <Alert tone="warning" title="Discord hasn't allowed message text yet">
            Posts are saved without their text and files until Discord allows the bot's Message
            Content. They fill in once it does.
          </Alert>
        )}
        {chosen.length === 0 ? (
          <p className="admin-note">No channels yet.</p>
        ) : (
          <ul className="admin-saved-channels">
            {chosen.map((channel, index) => {
              const live = options.get(channel.channelId) ?? named.get(channel.channelId)
              const name = live?.name ?? channel.channelId
              const deleted =
                !options.has(channel.channelId) && named.get(channel.channelId)?.deleted === true
              return (
                <li className="admin-saved-channel" key={channel.channelId}>
                  <div className="admin-saved-channel-name">
                    <strong>#{name}</strong>
                    <span className="admin-note">
                      {live ? KIND[live.kind] : "Channel"}
                      {live?.guildName ? ` · ${live.guildName}` : ""}
                    </span>
                    {deleted ? (
                      <span className="tny-warning">
                        #{name} was deleted in Discord. Its saved posts stay; nothing new is saved
                        from it.
                      </span>
                    ) : live && !live.botCanRead ? (
                      <span className="tny-warning">
                        {savedChannelHint({ missing: live.missing, name })}
                        {view.writable && live.reinstallUrl ? (
                          <>
                            {" "}
                            <a href={live.reinstallUrl}>Re-add the bot</a>
                          </>
                        ) : null}
                      </span>
                    ) : null}
                  </div>
                  <Checkbox
                    checked={channel.includeReplies}
                    disabled={!view.writable}
                    label="Include thread replies"
                    onChange={(event) =>
                      setChosen(
                        chosen.map((saved, at) =>
                          at === index ? { ...saved, includeReplies: event.target.checked } : saved,
                        ),
                      )
                    }
                  />
                  {view.writable ? (
                    <IconButton
                      icon={<NavIcon name="trash" />}
                      label={`Stop saving #${name}`}
                      onClick={() => setChosen(chosen.filter((_, at) => at !== index))}
                    />
                  ) : null}
                </li>
              )
            })}
          </ul>
        )}
        {view.writable ? (
          <div className="admin-row">
            <Field
              hint={full ? "Ternary saves at most 20 channels." : undefined}
              label="Add a channel"
            >
              <Select
                disabled={full || left.length === 0}
                onChange={(event) => setAdding(event.target.value)}
                value={adding}
              >
                <option value="">
                  {left.length === 0 ? "No other channels" : "Choose a channel"}
                </option>
                {left.map((option) => (
                  <option key={option.channelId} value={option.channelId}>
                    #{option.name} ({KIND[option.kind]}
                    {option.guildName ? `, ${option.guildName}` : ""})
                  </option>
                ))}
              </Select>
            </Field>
            <Button
              disabled={adding === ""}
              onClick={() => {
                const option = options.get(adding)
                if (!option) return
                setChosen([
                  ...chosen,
                  { channelId: option.channelId, guildId: option.guildId, includeReplies: true },
                ])
                setAdding("")
              }}
              size="sm"
              variant="secondary"
            >
              Add
            </Button>
          </div>
        ) : (
          <p className="admin-note">Only owners and admins can change this.</p>
        )}
        {view.writable ? (
          <div className="admin-row">
            <Button disabled={!changed} loading={busy} onClick={() => onSave(chosen)}>
              Save channels
            </Button>
          </div>
        ) : null}
      </div>
    </Card>
  )
}
